/**
 * ============================================================================
 *  File        : frontend/src/components/ResourceWorkbench.tsx
 *  Module      : components
 *  Purpose     : 可重用 CRUD workbench，主檔與夥伴管理共用
 *  Author      : Phoenix Lai
 *  Created     : 2026-05-30
 *  Updated     : 2026-05-30
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

import { Check, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react'
import { api, type Meta } from '../api/client'
import { useStepUp } from './StepUpDialog'
import type {
  ApiRow,
  FormValue,
  JsonValue,
  ResourceConfig,
  ResourceField,
  ResourceFormState,
} from '../types'

interface ResourceWorkbenchProps {
  config: ResourceConfig
  t: (key: string) => string
}

export function ResourceWorkbench({ config, t }: ResourceWorkbenchProps): ReactElement {
  const [rows, setRows] = useState<ApiRow[]>([])
  const [meta, setMeta] = useState<Meta | undefined>()
  const [selected, setSelected] = useState<ApiRow | null>(null)
  const [form, setForm] = useState<ResourceFormState>(() => initialForm(config.fields))
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // master / admin 寫入屬高風險端點（spec §9.2）：後端要求 2FA step-up code
  // （X-2FA-Code）。建立/更新/刪除前先彈出對話框取得當下有效 TOTP code。
  const stepUp = useStepUp(t)

  const canMutate = Boolean(config.createPath || config.updatePath)
  const canDelete = Boolean(config.deletePath)
  const showEditor = canMutate

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await api.listRows(config.listPath)
      setRows(result.data)
      setMeta(result.meta)
    } catch (loadError) {
      setError(readError(loadError, t('common.error')))
    } finally {
      setLoading(false)
    }
  }, [config.listPath, t])

  useEffect(() => {
    void load()
  }, [load])

  const selectedId = useMemo(() => {
    if (!selected) return null
    const value = selected[config.idKey]
    return typeof value === 'number' || typeof value === 'string' ? String(value) : null
  }, [config.idKey, selected])

  function startCreate(): void {
    setSelected(null)
    setForm(initialForm(config.fields))
    setNotice(null)
  }

  function startEdit(row: ApiRow): void {
    setSelected(row)
    setForm(formFromRow(config.fields, row))
    setNotice(null)
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    if (!canMutate) return
    // 先取得 step-up code，確認後才送出建立/更新。
    stepUp.request((code) => void submitWithCode(code))
  }

  async function submitWithCode(stepUpCode: string): Promise<void> {
    setSaving(true)
    setError(null)
    try {
      const payload = payloadFromForm(config, form)
      if (selectedId && config.updatePath) {
        await api.updateRow(config.updatePath.replace(':id', selectedId), payload, stepUpCode)
      } else if (config.createPath) {
        await api.createRow(config.createPath, payload, stepUpCode)
      }
      startCreate()
      setNotice(t('common.saved'))
      await load()
    } catch (saveError) {
      setError(readError(saveError, t('common.error')))
    } finally {
      setSaving(false)
    }
  }

  function handleDelete(row: ApiRow): void {
    if (!config.deletePath || !window.confirm(t('common.confirmDelete'))) return
    const idValue = row[config.idKey]
    if (typeof idValue !== 'number' && typeof idValue !== 'string') return
    stepUp.request((code) => void deleteWithCode(String(idValue), code))
  }

  async function deleteWithCode(idValue: string, stepUpCode: string): Promise<void> {
    if (!config.deletePath) return
    setSaving(true)
    setError(null)
    try {
      await api.deleteRow(config.deletePath.replace(':id', idValue), stepUpCode)
      await load()
    } catch (deleteError) {
      setError(readError(deleteError, t('common.error')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="workbench" aria-labelledby={`${config.id}-title`}>
      <header className="workbench-header">
        <div>
          <h2 id={`${config.id}-title`}>{t(config.titleKey)}</h2>
          <p>{t(config.descriptionKey)}</p>
        </div>
        <div className="toolbar">
          <button
            className="icon-button"
            type="button"
            data-testid={`${config.id}-refresh`}
            onClick={() => void load()}
          >
            <RefreshCw aria-hidden="true" size={18} />
            <span>{t('common.refresh')}</span>
          </button>
          {canMutate ? (
            <button
              className="icon-button primary"
              type="button"
              data-testid={`${config.id}-create`}
              onClick={startCreate}
            >
              <Plus aria-hidden="true" size={18} />
              <span>{t(config.primaryActionKey)}</span>
            </button>
          ) : null}
        </div>
      </header>

      <div className={`split-panel${showEditor ? '' : ' table-only'}`}>
        {showEditor ? (
          <form className="editor-panel" onSubmit={(event) => void handleSubmit(event)}>
            <div className="panel-heading">
              <h3>{t('common.form')}</h3>
              {selectedId ? (
                <button className="ghost-button" type="button" onClick={startCreate}>
                  <X aria-hidden="true" size={16} />
                  <span>{t('common.cancel')}</span>
                </button>
              ) : null}
            </div>
            <div className="form-grid">
              {config.fields.map((field) => (
                <FieldControl
                  key={field.key}
                  field={field}
                  value={form[field.key] ?? ''}
                  t={t}
                  onChange={(value) => {
                    setForm((current) => ({ ...current, [field.key]: value }))
                  }}
                />
              ))}
            </div>
            <button
              className="icon-button primary wide"
              disabled={saving}
              type="submit"
              data-testid={`${config.id}-submit`}
            >
              <Check aria-hidden="true" size={18} />
              <span>{selectedId ? t('common.update') : t(config.primaryActionKey)}</span>
            </button>
            {notice ? <p className="notice">{notice}</p> : null}
            {error ? <p className="error-text">{error}</p> : null}
          </form>
        ) : null}

        <div className="table-panel">
          <div className="panel-heading">
            <h3>{t('common.table')}</h3>
            {meta?.total !== undefined ? <span className="pill">{meta.total}</span> : null}
          </div>
          {loading ? <p className="muted">{t('common.loading')}</p> : null}
          {!loading && rows.length === 0 ? <p className="muted">{t(config.emptyKey)}</p> : null}
          {rows.length > 0 ? (
            <div className="data-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    {config.columns.map((column) => (
                      <th key={column}>{labelForColumn(column, t)}</th>
                    ))}
                    <th aria-label="actions" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={rowKey(row, config.idKey, index)}>
                      {config.columns.map((column) => (
                        <td key={column}>{displayValue(row[column])}</td>
                      ))}
                      <td className="row-actions">
                        {canMutate ? (
                          <button
                            className="square-button"
                            type="button"
                            data-testid={`${config.id}-edit-${rowKey(row, config.idKey, index)}`}
                            onClick={() => startEdit(row)}
                          >
                            <Pencil aria-hidden="true" size={16} />
                            <span className="sr-only">{t('common.edit')}</span>
                          </button>
                        ) : null}
                        {canDelete ? (
                          <button
                            className="square-button danger"
                            type="button"
                            onClick={() => void handleDelete(row)}
                          >
                            <Trash2 aria-hidden="true" size={16} />
                            <span className="sr-only">{t('common.delete')}</span>
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
      {stepUp.dialog}
    </section>
  )
}

function FieldControl({
  field,
  value,
  t,
  onChange,
}: {
  field: ResourceField
  value: string | boolean
  t: (key: string) => string
  onChange: (value: string | boolean) => void
}): ReactElement {
  const label = (
    <span>
      {t(field.labelKey)}
      {field.required ? <strong>{t('common.required')}</strong> : null}
    </span>
  )
  if (field.kind === 'checkbox') {
    return (
      <label className="checkbox-row">
        <input
          checked={value === true}
          type="checkbox"
          onChange={(event) => onChange(event.currentTarget.checked)}
        />
        {label}
      </label>
    )
  }
  if (field.kind === 'select') {
    return (
      <label>
        {label}
        <select
          required={field.required}
          value={String(value)}
          onChange={(event) => onChange(event.currentTarget.value)}
        >
          <option value="" />
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.labelKey)}
            </option>
          ))}
        </select>
      </label>
    )
  }
  if (field.kind === 'textarea') {
    return (
      <label className="span-2">
        {label}
        <textarea
          placeholder={field.placeholderKey ? t(field.placeholderKey) : undefined}
          required={field.required}
          value={String(value)}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      </label>
    )
  }
  return (
    <label>
      {label}
      <input
        inputMode={field.kind === 'number' ? 'decimal' : undefined}
        placeholder={field.placeholderKey ? t(field.placeholderKey) : undefined}
        required={field.required}
        type={field.kind === 'number' ? 'number' : 'text'}
        value={String(value)}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    </label>
  )
}

function initialForm(fields: ResourceField[]): ResourceFormState {
  return fields.reduce<ResourceFormState>((acc, field) => {
    acc[field.key] = field.kind === 'checkbox' ? true : ''
    return acc
  }, {})
}

function formFromRow(fields: ResourceField[], row: ApiRow): ResourceFormState {
  return fields.reduce<ResourceFormState>((acc, field) => {
    const value = row[field.key]
    if (field.kind === 'checkbox') {
      acc[field.key] = value === true
    } else {
      acc[field.key] =
        value === undefined || value === null || typeof value === 'object' ? '' : String(value)
    }
    return acc
  }, {})
}

function payloadFromForm(config: ResourceConfig, form: ResourceFormState): ApiRow {
  if (config.id === 'cases' || config.id === 'receiving') {
    return formalCasePayload(form)
  }
  return config.fields.reduce<ApiRow>((acc, field) => {
    const value = form[field.key]
    if (field.kind === 'checkbox') {
      acc[field.key] = value === true
    } else if (field.kind === 'number') {
      const text = typeof value === 'string' ? value.trim() : ''
      acc[field.key] = text === '' ? null : Number(text)
    } else {
      const text = typeof value === 'string' ? value.trim() : ''
      acc[field.key] = text === '' ? null : text
    }
    return acc
  }, {})
}

function formalCasePayload(form: ResourceFormState): ApiRow {
  const packagingType = asText(form['packaging_type'])
  return {
    seller_name: asText(form['seller_name']),
    listing_notes: asNullableText(form['listing_notes']),
    items: [
      {
        item_name: asText(form['item_name']),
        declared_weight_kg: asText(form['declared_weight_kg']),
        desired_unit_price: asText(form['desired_unit_price']),
        quantity: asText(form['quantity']),
        quantity_unit: asText(form['quantity_unit']) || 'kg',
        packaging_type: packagingType,
        bag_unit_weight_kg: defaultBagWeight(packagingType),
        pallet_measured_weight_kg: null,
        pallet_weight_pending: packagingType === 'pallet',
        item_notes: null,
        metadata_json: {
          actual_weight_kg: asNullableText(form['actual_weight_kg']),
        },
      },
    ],
    attachments: [],
    metadata_json: null,
  }
}

function defaultBagWeight(packagingType: string): string | null {
  if (packagingType === 'small_flexible') return '2.0'
  if (packagingType === 'flexible') return '3.0'
  if (packagingType === 'bara') return '0'
  return null
}

function asText(value: FormValue | undefined): string {
  return typeof value === 'string' ? value.trim() : ''
}

function asNullableText(value: FormValue | undefined): string | null {
  const text = asText(value)
  return text === '' ? null : text
}

function displayValue(value: JsonValue | undefined): string {
  if (value === undefined || value === null || value === '') return '-'
  if (Array.isArray(value)) return String(value.length)
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function rowKey(row: ApiRow, idKey: string, index: number): string {
  const id = row[idKey]
  if (typeof id === 'string' || typeof id === 'number') return String(id)
  return `row-${index}`
}

function labelForColumn(column: string, t: (key: string) => string): string {
  const mapped = columnKeyMap[column]
  return mapped ? t(mapped) : column
}

function readError(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message
  return fallback
}

const columnKeyMap: Record<string, string> = {
  id: 'field.id',
  code: 'field.code',
  org_code: 'field.orgCode',
  org_type: 'field.orgType',
  legal_name: 'field.legalName',
  country_code: 'field.countryCode',
  registration_no: 'field.registrationNo',
  tax_id: 'field.taxId',
  name: 'field.name',
  name_zh_tw: 'field.nameZh',
  name_ja: 'field.nameJa',
  name_en: 'field.nameEn',
  type: 'field.type',
  role: 'field.role',
  status: 'field.status',
  organization_id: 'field.organizationId',
  primary_organization_id: 'field.primaryOrganizationId',
  venue_id: 'field.venueId',
  material_code: 'field.materialCode',
  venue_code: 'field.venueCode',
  scale_code: 'field.scaleCode',
  warehouse_code: 'field.warehouseCode',
  display_name: 'field.displayName',
  is_system: 'field.isSystem',
  email: 'field.email',
  locale: 'field.locale',
  address: 'field.address',
  lot_no: 'field.lotNo',
  case_no: 'field.caseNo',
  seller_name: 'field.sellerName',
  management_code: 'field.managementCode',
  material_name: 'field.material',
  item_name: 'field.material',
  quantity: 'field.quantity',
  quantity_unit: 'field.quantityUnit',
  target_price: 'field.targetPrice',
  actual_weight_kg: 'field.actualWeight',
  package_style: 'field.packageStyle',
  packaging_type: 'field.packageStyle',
  available_weight_kg: 'field.stockAvailable',
  stock_weight_kg: 'field.stockAvailable',
  reserved_weight_kg: 'field.stockReserved',
  sold_weight_kg: 'field.stockSold',
  sold: 'field.stockSold',
  warehouse_name: 'master.warehouses',
  auditable_type: 'field.auditableType',
  auditable_id: 'field.auditableId',
  event_name: 'field.eventName',
  created_at: 'field.createdAt',
  message: 'field.message',
  is_active: 'field.status',
  is_bonded: 'field.isBonded',
}
